import { useState } from 'react';
import type { ReactNode } from 'react';
import { Modal } from '../../components/ui/Modal';
import { fieldClass } from '../../components/ui/form-classes';
import { canSeeRoleBadge, ROLE_LABELS } from '../../core/permissions';
import { preferencesOf, updateOwnProfile } from '../../db/auth-store';
import type { User } from '../../types/user';

interface ProfileModalProps {
  user: User;
  onClose: () => void;
  /** Hands the saved record back to the shell, so the header updates at once. */
  onSaved: (user: User) => void | Promise<void>;
}

function Labelled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-ink/60 mb-1">{label}</span>
      {children}
    </label>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 rounded-xl border border-line p-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 w-4 h-4 accent-brand-500"
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-ink/50">{hint}</span>
      </span>
    </label>
  );
}

/**
 * "עריכת פרטים אישיים" — the operator's own name, phone and display choices.
 *
 * The account's standing is deliberately absent: role, approval and permission
 * flags belong to the management screens, and `updateOwnProfile()` copies them
 * through untouched no matter what this form sends.
 */
export function ProfileModal({ user, onClose, onSaved }: ProfileModalProps) {
  const stored = preferencesOf(user);
  const [fullName, setFullName] = useState(user.fullName);
  const [phone, setPhone] = useState(user.phone);
  const [showRoleBadge, setShowRoleBadge] = useState(stored.showRoleBadge);
  const [showUnitLine, setShowUnitLine] = useState(stored.showUnitLine);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Only the super-admin is ever offered the badge switch — for everyone else
  // the header carries no role badge at all.
  const offersRoleBadge = canSeeRoleBadge(user);

  const handleSubmit = async () => {
    setError(null);
    setSaving(true);
    try {
      const updated = await updateOwnProfile(user, {
        fullName,
        phone,
        preferences: { showRoleBadge, showUnitLine },
      });
      await onSaved(updated);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="עריכת פרטים אישיים"
      subtitle={`${ROLE_LABELS[user.role]} · מספר אישי ${user.personalNumber}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
          >
            ביטול
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSubmit()}
            className="h-10 px-5 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? 'שומר…' : 'שמירה'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <Labelled label="שם מלא">
          <input
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            className={fieldClass}
          />
        </Labelled>

        <Labelled label="מספר טלפון">
          <input
            type="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            data-dir="ltr"
            className={fieldClass}
          />
        </Labelled>
        <p className="text-xs text-ink/50">
          מספר הטלפון הוא גם שם המשתמש לכניסה — שינוי שלו משנה את מספר הכניסה למערכת.
        </p>

        <fieldset className="space-y-2 pt-1">
          <legend className="text-xs text-ink/60 mb-1">העדפות תצוגה</legend>
          {offersRoleBadge ? (
            <Toggle
              label='הצגת תג "מנהל על" בכותרת'
              hint="התג מוצג לך בלבד ואינו נראה לחיילים או למנהלי העבודה."
              checked={showRoleBadge}
              onChange={setShowRoleBadge}
            />
          ) : null}
          <Toggle
            label="הצגת פלוגה ומחלקה בכותרת"
            hint="שורה נוספת מתחת לשם בראש המסך."
            checked={showUnitLine}
            onChange={setShowUnitLine}
          />
        </fieldset>

        {error ? (
          <p className="text-sm rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-3">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
